import { useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import type { NamedItem, Occurrence, OccurrenceDraft, User } from '../../types';
import { Button, ModalBackdrop, Select, TextInput } from '../ui';
import { cn } from '../../lib/cn';
import { fmtDateTime } from '../../lib/occurrences';

interface OccurrenceModalProps {
  occurrence: Occurrence | null; // null = nova
  cities: NamedItem[];
  occurrenceTypes: NamedItem[];
  users: User[];
  onClose: () => void;
  onSave: (draft: OccurrenceDraft) => Promise<void>;
}

const EMPTY: OccurrenceDraft = {
  contrato: '',
  nomeCliente: '',
  endereco: '',
  bairro: '',
  cidade: '',
  tempoCliente: '',
  primeiraOS: '',
  board: '',
  port: '',
  serial: '',
  ocorrencia: '',
  localizacao: '',
  status: 'Pendente',
  tecnico: '',
  situacao: ''
};

function toDraft(o: Occurrence): OccurrenceDraft {
  return {
    _id: o._id,
    contrato: o.contrato || '',
    nomeCliente: o.nomeCliente || '',
    endereco: o.endereco || '',
    bairro: o.bairro || '',
    cidade: o.cidade || '',
    tempoCliente: o.tempoCliente || '',
    primeiraOS: o.primeiraOS ? new Date(o.primeiraOS).toISOString().slice(0, 10) : '',
    board: o.board || '',
    port: o.port || '',
    serial: o.serial || '',
    ocorrencia: o.ocorrencia || '',
    localizacao: o.localizacao || '',
    status: o.status || 'Pendente',
    tecnico: o.tecnico || '',
    situacao: o.situacao || ''
  };
}

// Validação espelhando o backend, para feedback imediato.
function validate(d: OccurrenceDraft): string | null {
  if (!d.contrato.trim() || !/^\d+$/.test(d.contrato.trim())) return 'Contrato deve conter apenas números.';
  if (!d.nomeCliente.trim()) return 'Nome do cliente é obrigatório.';
  if (!d.endereco.trim()) return 'Endereço é obrigatório.';
  if (!d.bairro.trim()) return 'Bairro é obrigatório.';
  if (!d.cidade) return 'Selecione uma cidade.';
  if (!d.tempoCliente) return 'Selecione tempo de cliente.';
  if (d.tempoCliente === 'ANTIGO') {
    if (!d.primeiraOS) return 'Informe a data de abertura da 1ª O.S. (cliente antigo).';
    const dt = new Date(d.primeiraOS);
    if (isNaN(dt.getTime()) || dt.getTime() > Date.now()) return 'A data da 1ª O.S. é inválida ou está no futuro.';
  }
  if (!d.ocorrencia) return 'Selecione a ocorrência.';
  if (d.board && !/^\d+$/.test(d.board)) return 'Board: use apenas números inteiros.';
  if (d.port && !/^\d+$/.test(d.port)) return 'Port: use apenas números inteiros.';
  if (!d.serial.trim()) return 'Serial do equipamento é obrigatório.';
  if (d.serial.replace(/\s/g, '').length < 5) return 'Informe ao menos os últimos 5 dígitos do serial do equipamento.';
  return null;
}

export default function OccurrenceModal({ occurrence, cities, occurrenceTypes, users, onClose, onSave }: OccurrenceModalProps) {
  const [draft, setDraft] = useState<OccurrenceDraft>(occurrence ? toDraft(occurrence) : EMPTY);
  const [saving, setSaving] = useState(false);

  const techOptions = useMemo(
    () => (users || []).filter((u) => u.ativo !== false).map((u) => u.username).sort(),
    [users]
  );

  const set = (k: keyof OccurrenceDraft, v: string) => setDraft((p) => ({ ...p, [k]: v }));
  const label = 'text-xs font-semibold text-slate-600';

  const submit = async () => {
    const err = validate(draft);
    if (err) {
      window.alert(err);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
    } catch (e) {
      window.alert((e as Error).message || 'Erro ao salvar OS.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-bold text-slate-800">
            {occurrence ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço'}
          </h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <FiX size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={label}>Contrato *</label>
              <TextInput value={draft.contrato} inputMode="numeric" onChange={(e) => set('contrato', e.target.value)} placeholder="Somente números" />
            </div>
            <div>
              <label className={label}>Cliente *</label>
              <TextInput value={draft.nomeCliente} onChange={(e) => set('nomeCliente', e.target.value)} />
            </div>
            <div>
              <label className={label}>Endereço *</label>
              <TextInput value={draft.endereco} onChange={(e) => set('endereco', e.target.value)} />
            </div>
            <div>
              <label className={label}>Bairro *</label>
              <TextInput value={draft.bairro} onChange={(e) => set('bairro', e.target.value)} />
            </div>
            <div>
              <label className={label}>Cidade *</label>
              <Select value={draft.cidade} onChange={(e) => set('cidade', e.target.value)}>
                <option value="">Selecione</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className={label}>Tempo de cliente *</label>
              <Select value={draft.tempoCliente} onChange={(e) => set('tempoCliente', e.target.value)}>
                <option value="">Selecione</option>
                <option value="NOVO">NOVO</option>
                <option value="ANTIGO">ANTIGO</option>
              </Select>
            </div>
            {draft.tempoCliente === 'ANTIGO' && (
              <div>
                <label className={label}>Abertura da 1ª O.S. *</label>
                <TextInput
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={draft.primeiraOS}
                  onChange={(e) => set('primeiraOS', e.target.value)}
                />
              </div>
            )}
            <div>
              <label className={label}>Ocorrência *</label>
              <Select value={draft.ocorrencia} onChange={(e) => set('ocorrencia', e.target.value)}>
                <option value="">Selecione</option>
                {occurrenceTypes.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className={label}>Board</label>
              <TextInput value={draft.board} inputMode="numeric" onChange={(e) => set('board', e.target.value)} />
            </div>
            <div>
              <label className={label}>Port</label>
              <TextInput value={draft.port} inputMode="numeric" onChange={(e) => set('port', e.target.value)} />
            </div>
            <div>
              <label className={label}>Serial *</label>
              <TextInput value={draft.serial} onChange={(e) => set('serial', e.target.value)} placeholder="Últimos 5+ dígitos" />
            </div>
            <div>
              <label className={label}>Situação</label>
              <Select value={draft.situacao} onChange={(e) => set('situacao', e.target.value)}>
                <option value="">Não informada</option>
                <option value="Conectado">Conectado</option>
                <option value="Desconectado">Desconectado</option>
              </Select>
            </div>
            <div>
              <label className={label}>Técnico</label>
              <Select value={draft.tecnico} onChange={(e) => set('tecnico', e.target.value)}>
                <option value="">Não atribuído</option>
                {techOptions.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className={label}>Status</label>
              <Select value={draft.status} onChange={(e) => set('status', e.target.value)}>
                <option value="Pendente">Pendente</option>
                <option value="Concluído">Concluído</option>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className={label}>Localização (link do Google Maps)</label>
              <TextInput value={draft.localizacao} onChange={(e) => set('localizacao', e.target.value)} placeholder="https://maps.google.com/..." />
            </div>
          </div>

          {occurrence && occurrence.historico?.length > 0 && (
            <div className="mt-5">
              <span className={label}>Histórico</span>
              <div className="mt-1 space-y-1">
                {[...occurrence.historico]
                  .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime())
                  .map((h, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-1.5 text-xs"
                    >
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 font-semibold',
                          h.acao === 'Criado'
                            ? 'bg-blue-100 text-blue-700'
                            : h.acao === 'Concluído'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                        )}
                      >
                        {h.acao}
                      </span>
                      <span className="text-slate-600">{h.usuario}</span>
                      <span className="ml-auto text-slate-400">{fmtDateTime(h.ts)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar OS'}
          </Button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
